import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {BuilderCanvasComponent} from '../builder-canvas/builder-canvas.component';
import {BlockEditorComponent} from '../block-editor/block-editor.component';
import {DynamicFormComponent} from '../../../runtime/components/dynamic-form/dynamic-form.component';
import {BuilderStateService} from '../../services/builder-state.service';
import {Observable, Subscription} from 'rxjs';
import {FormDefinition} from '../../../shared/models/form-schema.model';
import {FormsModule} from '@angular/forms';

type MobileView = 'editor' | 'preview';

@Component({
  selector: 'forms-form-preview-split-view',
  standalone: true,
  imports: [
    CommonModule,
    BuilderCanvasComponent,
    BlockEditorComponent,
    DynamicFormComponent,
    FormsModule
  ],
  template: `
    <div class="flex flex-col lg:flex-row h-full max-h-full w-full overflow-hidden bg-gray-50/50 text-gray-900">

      <!-- Mobile segmented switch -->
      <div class="lg:hidden flex-shrink-0 flex items-center gap-1 p-2 bg-white border-b border-gray-100">
        <button (click)="showCanvas()" [class]="tabClass(mobileView === 'editor' && leftViewMode === 'canvas')">
          Bearbeiten
        </button>
        <button (click)="showPreview()" [class]="tabClass(mobileView === 'preview')">
          Vorschau
        </button>
        <button (click)="showJson()" [class]="tabClass(mobileView === 'editor' && leftViewMode === 'json')">
          JSON
        </button>
      </div>

      <!-- Left: Builder Canvas / JSON -->
      <div [ngClass]="{'flex': mobileView === 'editor', 'hidden': mobileView !== 'editor'}"
           class="lg:flex flex-col flex-1 min-w-0 border-r border-gray-200 lg:shadow-lg relative z-0 transition-all duration-300 ease-in-out">

        <!-- Desktop pane toolbar -->
        <div class="hidden lg:flex h-14 items-center justify-between px-6 bg-white border-b border-gray-100 flex-shrink-0">
          <div class="flex p-1 bg-gray-200/50 rounded-lg">
            <button (click)="leftViewMode = 'canvas'"
                    [class.bg-white]="leftViewMode === 'canvas'" [class.shadow-sm]="leftViewMode === 'canvas'"
                    [class.text-gray-900]="leftViewMode === 'canvas'" [class.text-gray-500]="leftViewMode !== 'canvas'"
                    class="px-3 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition-all duration-200">
              Canvas
            </button>
            <button (click)="leftViewMode = 'json'"
                    [class.bg-white]="leftViewMode === 'json'" [class.shadow-sm]="leftViewMode === 'json'"
                    [class.text-gray-900]="leftViewMode === 'json'" [class.text-gray-500]="leftViewMode !== 'json'"
                    class="px-3 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition-all duration-200">
              JSON Editor
            </button>
          </div>
          <span *ngIf="leftViewMode === 'canvas'"
                class="text-[10px] text-gray-400 font-bold uppercase tracking-widest px-2 py-1 bg-gray-50 rounded-md border border-gray-100">Live Modus</span>
          <span *ngIf="leftViewMode === 'json'"
                class="text-[10px] text-amber-500 font-bold uppercase tracking-widest px-2 py-1 bg-amber-50 rounded-md border border-amber-100">Experten Modus</span>
        </div>

        <!-- Content Area -->
        <div class="flex-grow overflow-hidden flex flex-col">
          @switch (leftViewMode) {
            @case ('canvas') {
              <div class="flex-grow overflow-y-auto scrollbar-hide">
                <div class="max-w-4xl mx-auto py-4 px-3 sm:px-5">
                  <forms-builder-canvas></forms-builder-canvas>
                </div>
              </div>
            }
            @case ('json') {
              <div class="flex-grow flex flex-col p-3 sm:p-6 bg-gray-900 overflow-hidden">
                <textarea
                  class="flex-grow w-full h-full bg-transparent text-gray-300 font-mono text-xs sm:text-sm resize-none focus:outline-none scrollbar-custom p-4 border border-gray-800 rounded-xl"
                  [ngModel]="jsonContent" (ngModelChange)="onJsonChange($event)" spellcheck="false"></textarea>
                @if (jsonError) {
                  <div class="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs font-medium">
                    {{ jsonError }}
                  </div>
                }
              </div>
            }
          }
        </div>
      </div>

      <!-- Right: Preview -->
      <div [ngClass]="{'flex': mobileView === 'preview', 'hidden': mobileView !== 'preview'}"
           class="lg:flex flex-col flex-1 min-w-0 bg-white overflow-hidden relative transition-all duration-300 ease-in-out">

        <div class="hidden lg:flex h-14 items-center justify-between px-6 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <div class="flex items-center gap-2">
            <h2 class="text-sm font-semibold tracking-tight text-gray-700">Vorschau</h2>
            <div class="w-1 h-1 bg-gray-300 rounded-full"></div>
            <span class="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Live</span>
          </div>
          <div class="flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-600 rounded-full border border-blue-100/50">
            <div class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
            <span class="text-[10px] font-bold uppercase tracking-wider">Echtzeit Sync</span>
          </div>
        </div>

        <div class="flex-grow overflow-y-auto bg-gray-50/30 p-3 sm:p-8">
          <div class="max-w-2xl mx-auto">
            <forms-dynamic-form *ngIf="state.previewDefinition$ | async as def" [definition]="def"></forms-dynamic-form>
          </div>
        </div>
      </div>

      <!-- Block Editor: docked column on desktop, bottom sheet on mobile -->
      @if (state.editingBlock$ | async) {
        <div class="lg:hidden fixed inset-0 bg-black/30 z-30 animate-in fade-in duration-200"
             (click)="state.stopEditingBlock()"></div>
        <aside
          class="z-40 bg-white shadow-2xl flex flex-col
                 fixed inset-x-0 bottom-0 h-[85vh] rounded-t-3xl
                 lg:static lg:inset-auto lg:h-full lg:rounded-none lg:w-[26rem] lg:flex-shrink-0 lg:border-l lg:border-gray-100
                 animate-in slide-in-from-bottom lg:slide-in-from-right duration-300 ease-out">
          <div class="lg:hidden mx-auto mt-2 mb-1 h-1.5 w-12 rounded-full bg-gray-200 flex-shrink-0"></div>
          <forms-block-editor class="flex-1 min-h-0"></forms-block-editor>
        </aside>
      }
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      height: 100%;
      width: 100%;
    }

    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }

    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .scrollbar-custom::-webkit-scrollbar {
      width: 8px;
    }

    .scrollbar-custom::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 10px;
    }

    .scrollbar-custom::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
    }

    .scrollbar-custom::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `]
})
export class FormPreviewSplitViewComponent implements OnInit, OnDestroy {
  definition$: Observable<FormDefinition>;
  leftViewMode: 'canvas' | 'json' = 'canvas';
  mobileView: MobileView = 'editor';
  jsonContent: string = '';
  jsonError: string | null = null;
  private sub = new Subscription();

  constructor(public state: BuilderStateService, private cdr: ChangeDetectorRef) {
    // Dependency injection only. `definition$` is a plain alias — no subscription, no change detection: see
    // ngOnInit for why (#466).
    this.definition$ = this.state.definition$;
  }

  /**
   * Mirrors the canonical definition into the JSON editor.
   *
   * **Why this is in `ngOnInit` and uses `markForCheck` (#466).** It used to run from the *constructor* and call
   * `detectChanges()`. Both were wrong, in ways worth naming because the bug report guessed at a third thing:
   *
   * - `state.definition$` is a `BehaviorSubject`, so subscribing in the constructor fires **synchronously
   *   during construction** — and `detectChanges()` on a view Angular has not created yet renders nothing at
   *   all. It was measurably a no-op (the template's `tabClass` binding was never evaluated by it), so it read
   *   like a safeguard while doing nothing.
   * - `detectChanges()` is the wrong primitive for what this does. It forces a **synchronous, re-entrant** check
   *   of this view from inside an arbitrary emission — and emissions arrive from descendant components' event
   *   handlers (the canvas adding a block, the block editor saving, undo/redo). Nothing here needs a synchronous
   *   flush: the only thing written is {@link jsonContent}, a plain field bound with `[ngModel]`, which the next
   *   pass renders. `markForCheck()` states exactly that, cannot re-enter change detection, and is a safe no-op
   *   on a destroyed view — so a late emission can no longer tear the subscription down with a throw.
   */
  ngOnInit() {
    this.sub.add(this.definition$.subscribe(def => {
      const newJson = JSON.stringify(def, null, 2);
      if (this.leftViewMode !== 'json') {
        this.jsonContent = newJson;
        this.cdr.markForCheck();
      } else if (this.jsonContent !== newJson) {
        // In JSON mode, only refresh the editor if the external state is valid
        // and logically different, so we never clobber in-progress edits.
        try {
          const currentParsed = JSON.parse(this.jsonContent);
          if (JSON.stringify(currentParsed) !== JSON.stringify(def)) {
            this.jsonContent = newJson;
            this.cdr.markForCheck();
          }
        } catch {
          // current JSON is mid-edit; leave it alone
        }
      }
    }));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  tabClass(active: boolean): string {
    const base = 'flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200';
    return active
      ? `${base} bg-theme-primary-600 text-white shadow-sm`
      : `${base} text-gray-500 hover:bg-gray-100`;
  }

  showCanvas() {
    this.mobileView = 'editor';
    this.leftViewMode = 'canvas';
  }

  showJson() {
    this.mobileView = 'editor';
    this.leftViewMode = 'json';
  }

  showPreview() {
    this.mobileView = 'preview';
  }

  onJsonChange(newVal: string) {
    this.jsonContent = newVal;
    try {
      const parsed = JSON.parse(newVal);
      this.state.setDefinition({...parsed});
      this.jsonError = null;
    } catch (e: any) {
      this.jsonError = `JSON Fehler: ${e.message}`;
    }
  }
}
